<template>
  <div class="hub-search-wrap">
    <button
      type="button"
      class="hub-search"
      :class="{ 'hub-search--compact': compact }"
      aria-haspopup="dialog"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="'Suchen (' + shortcutText + ')'"
      :aria-keyshortcuts="mac ? 'Meta+K' : 'Control+K'"
      @click="show"
      @keydown.enter="onTriggerKeydown"
    >
      <app-icon name="search" :size="14" class="hub-search__icon" />
      <span class="hub-search__label" aria-hidden="true">Suchen …</span>
      <span class="hub-search__kbd" aria-hidden="true">{{ mac ? '⌘K' : 'Strg K' }}</span>
    </button>

    <v-dialog
      v-model="open"
      :max-width="512"
      content-class="app-hub-search-dialog"
      overlay-color="var(--scrim)"
      overlay-opacity="1"
    >
      <div ref="surface" class="hub-cmd">
        <div class="hub-cmd__field">
          <app-icon name="search" :size="16" class="hub-cmd__field-icon" />
          <input
            ref="input"
            v-model="query"
            type="text"
            class="hub-cmd__input"
            role="combobox"
            aria-label="Navigation durchsuchen"
            aria-autocomplete="list"
            :aria-expanded="results.length ? 'true' : 'false'"
            :aria-controls="listId"
            :aria-activedescendant="activeId"
            placeholder="Navigation durchsuchen …"
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            enterkeyhint="go"
            @keydown="onKeydown"
          />
        </div>
        <div
          v-show="results.length"
          :id="listId"
          ref="list"
          class="hub-cmd__list"
          role="listbox"
          aria-label="Navigation"
          tabindex="-1"
        >
          <div class="hub-cmd__heading" aria-hidden="true">Navigation</div>
          <div
            v-for="(item, index) in results"
            :id="optionId(item)"
            :key="item.key"
            class="hub-cmd__option"
            :class="{
              'hub-cmd__option--active': index === active,
              'hub-cmd__option--keyboard': index === active && keyboard
            }"
            role="option"
            :aria-selected="index === active ? 'true' : 'false'"
            :aria-label="item.group ? item.title + ', ' + item.group : item.title"
            @mousemove="point(index)"
            @mousedown.prevent
            @click="go(item)"
          >
            <span class="hub-cmd__title">{{ item.title }}</span>
            <span v-if="item.group" class="hub-cmd__group">{{ item.group }}</span>
          </div>
        </div>
        <p v-if="!results.length" class="hub-cmd__empty" aria-hidden="true">Keine Treffer</p>
        <p class="app-sr-only" role="status">{{ status }}</p>
        <button type="button" class="hub-cmd__esc" @click="open = false">
          Esc<span class="app-sr-only"> – Suche schließen</span>
        </button>
      </div>
    </v-dialog>
  </div>
</template>

<script>
// Suche der Hub-Kopfleiste (rechts vor dem Avatar), Aufbau und Maße wie Suchpille und Befehlsdialog des Mobility Hub
// (Analyse „topbarRight“ 1 vom 10.10.2026), Farben aus tokens.css. Durchsucht die Navigation (navigation.js).
// Knopf ab 640 px als Pille 192 × 32 (Touch 192 × 44): rund, Kante --line, --surface-raised (hell weiß), Lupe 14,
// „Suchen …“ 12/16 --ink-3 und Tastenchip „⌘K“ (macOS) bzw. „Strg K“ (sonst; 10 px 500 auf --tint, Radius 4); Hover
// --hov mit Text --ink,
// gedrückt --press, Fokus 2 px --link mit Abstand 2. Unter 640 px (oder mit compact) runder Icon-Knopf 32 (Touch 44).
// Klick oder ⌘K bzw. Strg+K öffnet den Dialog (globaler keydown-Listener, solange die Komponente steht; die Tasten
// schalten um; nicht, wenn schon ein anderer Dialog oder ein modales DevExtreme-Popup offen ist, und nicht in
// Editoren mit contenteditable, wo Strg+K einen Link setzt).
// Dialog (v-dialog): 512 breit (schmaler: Fensterbreite − 32), oben auf der Höhe, auf der der volle Dialog mittig
// stünde (die Liste schrumpft beim Filtern nach unten, das Feld springt nicht; unter 640 px 16 vom oberen Rand),
// Radius 10, Kante --line, Schatten --sh-overlay, Scrim --scrim. Eingabezeile 48 mit Lupe 16, 14 px (Touch 16 px,
// sonst zoomt iOS), Platzhalter „Navigation durchsuchen …“; im Fokus wird ihre Unterkante zur Linie 2 px --link.
// Chip „Esc“ oben rechts (12/12, 24 hoch, schließt). Liste höchstens 300 hoch mit Kopf „Navigation“ (12/16 500) und
// Einträgen 44 (Radius 6, 14/20): Seite links, Gruppe rechts in 12 px --ink-3 – alle Seiten aus
// visibleNavigation(this.$router), auch die Übersicht (ohne Gruppe). Filter ohne Groß-/Kleinschreibung auf Seite und
// Gruppe, jedes Wort muss treffen; „ue“, „u“ und „ü“ gelten gleich. Seitentitel, die mit der Eingabe beginnen, stehen
// vorn, dann Titel, die sie enthalten, dann Treffer nur über die Gruppe (sonst Reihenfolge der Navigation).
// Der erste Treffer ist vorausgewählt (--sel); Pfeil hoch/runter wählt (nach Tastatur zusätzlich Ring 2 px --link wie
// in den Menüs; die Liste scrollt nur dabei mit), Mausbewegung wählt, Enter oder Klick navigiert mit $router.push und
// schließt, Esc oder Klick auf den
// Scrim schließt; danach kehrt der Fokus zurück. Ohne Treffer „Keine Treffer“. Bedienung als Combobox mit Listbox
// (aria-activedescendant); die Trefferzahl wird verzögert angesagt.
// Props: shortcut (Boolean, Standard true: ⌘K/Strg+K aktiv), compact (Boolean: immer runder Icon-Knopf).
// Ereignis navigate (Pfad) nach dem Navigieren. Methode show() öffnet den Dialog (z. B. aus einem Menü). Keine Slots.
import AppIcon from '../ui/AppIcon.vue';
import { visibleNavigation } from '../../shell/navigation';
import { notePopup } from '../../shell/shellState';

// macOS (auch iPadOS mit Tastatur): ⌘K, sonst Strg+K
function isMac() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const data = navigator.userAgentData;
  const platform = (data && data.platform) || navigator.platform || '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

function nfc(text) {
  const s = String(text || '');
  return typeof s.normalize === 'function' ? s.normalize('NFC') : s;
}

// Vergleichsformen: klein mit ausgeschriebenen Umlauten („Übersicht“ → „uebersicht“) und klein ohne Umlautpunkte
// („ubersicht“), ß jeweils als ss
function spelled(text) {
  return nfc(text).toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
}

function plain(text) {
  const s = nfc(text).toLowerCase().replace(/ß/g, 'ss');
  return typeof s.normalize === 'function' ? s.normalize('NFD').replace(/[̀-ͯ]/g, '') : s;
}

function forms(text) {
  return { a: spelled(text), b: plain(text) };
}

function contains(hay, needle) {
  return hay.a.indexOf(needle.a) !== -1 || hay.b.indexOf(needle.b) !== -1;
}

function startsWith(hay, needle) {
  return hay.a.indexOf(needle.a) === 0 || hay.b.indexOf(needle.b) === 0;
}

function makeItem(key, title, group, path) {
  return { key, title, group, path, titleForms: forms(title), allForms: forms(group + ' ' + title) };
}

export default {
  name: 'AppHubSearch',
  components: { AppIcon },
  props: {
    // ⌘K bzw. Strg+K öffnet und schließt den Dialog (nur eine Instanz sollte das tun)
    shortcut: { type: Boolean, default: true },
    // immer als runder Icon-Knopf, auch ab 640 px
    compact: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      query: '',
      active: 0,
      // zuletzt per Pfeiltaste gewählt: Ring wie in den Menüs
      keyboard: false,
      status: '',
      mac: isMac()
    };
  },
  computed: {
    shortcutText() {
      return this.mac ? '⌘K' : 'Strg+K';
    },
    // alle sichtbaren Seiten in der Reihenfolge der Navigation (reaktiv auf Profil und Rechte)
    items() {
      const list = [];
      visibleNavigation(this.$router).forEach((group) => {
        if (group.children) {
          group.children.forEach((entry, index) => {
            list.push(makeItem(group.id + '-' + index, entry.title, group.title, entry.path));
          });
        } else if (group.path) {
          list.push(makeItem(group.id, group.title, '', group.path));
        }
      });
      return list;
    },
    results() {
      const query = this.query.trim();
      if (!query) {
        return this.items;
      }
      const words = query.split(/\s+/).map(forms);
      const whole = forms(query);
      return this.items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => words.every(word => contains(item.allForms, word)))
        .map(({ item, index }) => {
          let rank = 2;
          if (startsWith(item.titleForms, whole)) {
            rank = 0;
          } else if (contains(item.titleForms, whole)) {
            rank = 1;
          }
          return { item, index, rank };
        })
        .sort((x, y) => x.rank - y.rank || x.index - y.index)
        .map(entry => entry.item);
    },
    activeId() {
      const item = this.results[this.active];
      return item ? this.optionId(item) : null;
    },
    listId() {
      return 'app-hub-search-' + this._uid + '-liste';
    }
  },
  watch: {
    open(value) {
      notePopup(value);
      if (value) {
        this.query = '';
        this.active = 0;
        this.keyboard = false;
        this.status = '';
        // v-dialog legt den Fokus erst nach zwei nextTicks auf seine Fläche; danach ins Eingabefeld
        window.clearTimeout(this.focusTimer);
        this.focusTimer = window.setTimeout(this.focusInput, 0);
      }
    },
    query() {
      this.active = 0;
      this.keyboard = false;
      this.$nextTick(this.reveal);
    },
    results() {
      if (this.active >= this.results.length) {
        this.active = 0;
      }
      this.announce();
    }
  },
  created() {
    // nicht reaktiv
    this.focusTimer = null;
    this.statusTimer = null;
  },
  mounted() {
    document.addEventListener('keydown', this.onShortcut);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onShortcut);
    window.clearTimeout(this.focusTimer);
    window.clearTimeout(this.statusTimer);
    if (this.open) {
      notePopup(false);
    }
  },
  methods: {
    show() {
      this.open = true;
    },
    optionId(item) {
      return 'app-hub-search-' + this._uid + '-' + item.key;
    },
    focusInput() {
      const input = this.$refs.input;
      const surface = this.$refs.surface;
      if (!this.open || !input) {
        return;
      }
      // Vuetify setzt role="dialog" am äußeren Inhaltselement; benennen, dann ins Feld
      const dialog = surface && surface.closest ? surface.closest('[role="dialog"]') : null;
      if (dialog) {
        dialog.setAttribute('aria-label', 'Navigation durchsuchen');
      }
      input.focus();
      // die Liste bleibt zwischen zwei Öffnungen stehen: wieder nach oben zum vorausgewählten ersten Eintrag
      this.reveal();
    },
    // gehaltene Enter-Taste nach dem Navigieren (der Fokus ist zurück auf dem Knopf) öffnet nicht erneut
    onTriggerKeydown(event) {
      if (event.repeat) {
        event.preventDefault();
      }
    },
    // ⌘K (macOS) bzw. Strg+K; auch bei anderen Tastaturbelegungen (Kyrillisch usw.) über die Taste K
    isShortcut(event) {
      const key = String(event.key || '').toLowerCase();
      const modifier = this.mac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
      const isK = key === 'k' || (!/^[a-z]$/.test(key) && event.code === 'KeyK');
      return modifier && !event.altKey && !event.shiftKey && isK;
    },
    onShortcut(event) {
      if (!this.shortcut || event.defaultPrevented || event.isComposing || !this.isShortcut(event)) {
        return;
      }
      if (!this.open) {
        const target = event.target;
        if (target && target.closest && target.closest('[contenteditable]:not([contenteditable="false"])')) {
          return;
        }
        if (this.otherDialogOpen()) {
          return;
        }
      }
      // auch bei Tastenwiederholung, sonst springt der Browser in seine eigene Suche
      event.preventDefault();
      if (!event.repeat) {
        this.open = !this.open;
      }
    },
    // Vuetify-Dialoge (auch „Mandant wechseln“) und modale DevExtreme-Popups
    otherDialogOpen() {
      return !!document.querySelector('.v-dialog__content--active, .dx-overlay-shader');
    },
    onKeydown(event) {
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      const count = this.results.length;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (count) {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.active = Math.min(Math.max(this.active + step, 0), count - 1);
          this.keyboard = true;
          // nur bei Tastatur mitscrollen (wie cmdk), die Maus wählt ohne die Liste zu bewegen
          this.$nextTick(this.reveal);
        }
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this.go(this.results[this.active]);
      }
    },
    point(index) {
      this.active = index;
      this.keyboard = false;
    },
    go(item) {
      if (!item) {
        return;
      }
      this.open = false;
      this.$router.push(item.path).then(() => {
        // der Dialog gibt den Fokus an das vorher fokussierte Element zurück; stand es auf der alten Seite, ist der Fokus
        // danach weg – dann auf den Inhalt der neuen Seite
        this.$nextTick(() => {
          const active = document.activeElement;
          const content = document.getElementById('inhalt');
          if ((!active || active === document.body) && content) {
            content.focus({ preventScroll: true });
          }
        });
      }).catch(() => {});
      this.$emit('navigate', item.path);
    },
    // gewählten Eintrag in der Liste sichtbar halten (beim ersten bleibt der Kopf „Navigation“ sichtbar)
    reveal() {
      const list = this.$refs.list;
      if (!list || !this.results.length) {
        return;
      }
      if (this.active === 0) {
        list.scrollTop = 0;
        return;
      }
      const option = list.querySelector('[aria-selected="true"]');
      if (!option) {
        return;
      }
      const top = option.offsetTop - 4;
      const bottom = option.offsetTop + option.offsetHeight + 4;
      if (top < list.scrollTop) {
        list.scrollTop = top;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    },
    // Trefferzahl für Screenreader, verzögert, damit nicht jeder Tastendruck angesagt wird
    announce() {
      window.clearTimeout(this.statusTimer);
      this.statusTimer = window.setTimeout(() => {
        const count = this.results.length;
        if (!this.query.trim()) {
          this.status = '';
        } else {
          this.status = count ? count + ' Treffer' : 'Keine Treffer';
        }
      }, 400);
    }
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------- Knopf: Icon-Knopf (schmal) bzw. Pille ab 640 px */
.hub-search-wrap {
  display: flex;
  flex: none;
}
.hub-search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: var(--h-ctl);
  height: var(--h-ctl);
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 9999px;
  background: var(--surface-raised);
  color: var(--ink-3);
  font-family: var(--font);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.hub-search__icon {
  flex: none;
  width: 16px;
  height: 16px;
}
html.dichte-touch .hub-search__icon {
  width: 20px;
  height: 20px;
}
.hub-search__label,
.hub-search__kbd {
  display: none;
}
:where(html:not(.dichte-touch)) .hub-search:hover {
  background: var(--hov);
  color: var(--ink);
}
.hub-search:active {
  background: var(--press);
  color: var(--ink);
}
.hub-search:focus {
  outline: none;
}
.hub-search:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
@media (min-width: 640px) {
  .hub-search:not(.hub-search--compact) {
    justify-content: flex-start;
    gap: 8px;
    width: 192px;
    padding: 0 6px 0 12px;
  }
  html.dichte-touch .hub-search:not(.hub-search--compact) {
    padding: 0 12px 0 16px;
  }
  .hub-search:not(.hub-search--compact) .hub-search__icon {
    width: 14px;
    height: 14px;
  }
  html.dichte-touch .hub-search:not(.hub-search--compact) .hub-search__icon {
    width: 16px;
    height: 16px;
  }
  .hub-search:not(.hub-search--compact) .hub-search__label {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    font-size: 12px;
    line-height: 16px;
  }
  html.dichte-touch .hub-search:not(.hub-search--compact) .hub-search__label {
    font-size: 14px;
    line-height: 20px;
  }
  .hub-search:not(.hub-search--compact) .hub-search__kbd {
    display: inline-flex;
    align-items: center;
    flex: none;
    padding: 2px 6px;
    border-radius: var(--r-inner);
    background: var(--tint);
    color: var(--ink-3);
    font-size: 10px;
    line-height: 16px;
    font-weight: 500;
    white-space: nowrap;
  }
}

/* ---------------------------------------------------------------- Dialog-Inhalt */
.hub-cmd {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  background: var(--surface-raised);
  color: var(--ink);
  font-family: var(--font);
  font-size: 14px;
  line-height: 20px;
}
/* Eingabezeile 48; rechts Platz für den Chip „Esc“ */
.hub-cmd__field {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  box-sizing: border-box;
  padding: 0 64px 0 12px;
  border-bottom: 1px solid var(--line);
}
.hub-cmd__field:focus-within {
  border-bottom-color: var(--link);
  box-shadow: inset 0 -1px 0 var(--link);
}
.hub-cmd__field-icon {
  flex: none;
  width: 16px;
  height: 16px;
  color: var(--ink-3);
}
.hub-cmd__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  color: var(--ink);
  font-family: var(--font);
  /* 14 px, bei Touch 16 px (--fs-in), sonst zoomt iOS beim Fokus */
  font-size: max(14px, var(--fs-in));
  line-height: 20px;
  -webkit-appearance: none;
  appearance: none;
}
.hub-cmd__input::placeholder {
  color: var(--ink-3);
  opacity: 1;
}
/* Fokus zeigt die Eingabezeile (Unterkante --link), kein Ring um das randlose Feld */
.hub-cmd__input:focus,
.hub-cmd__input:focus-visible {
  outline: none;
}

/* Liste höchstens 300 (schrumpft in niedrigen Fenstern), Polster 4 */
.hub-cmd__list {
  position: relative;
  flex: 0 1 auto;
  min-height: 0;
  max-height: 300px;
  box-sizing: border-box;
  padding: 4px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.hub-cmd__heading {
  padding: 6px 8px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ink-3);
  user-select: none;
}
.hub-cmd__option {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  box-sizing: border-box;
  padding: 0 8px;
  border-radius: var(--r-ctl);
  color: var(--ink);
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.hub-cmd__option--active {
  background: var(--sel);
}
.hub-cmd__option--keyboard {
  box-shadow: inset 0 0 0 2px var(--link);
}
.hub-cmd__title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hub-cmd__group {
  flex: none;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.hub-cmd__empty {
  margin: 0;
  padding: 24px 16px;
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  color: var(--ink-3);
}

/* Chip „Esc“ oben rechts (schließt); bei Touch Tippfläche 44 hoch */
.hub-cmd__esc {
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  height: 24px;
  box-sizing: border-box;
  margin: 0;
  padding: 0 8px;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  background: var(--hov);
  color: var(--ink-3);
  font-family: var(--font);
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .hub-cmd__esc:hover,
.hub-cmd__esc:active {
  background: var(--press);
  color: var(--ink);
}
.hub-cmd__esc:focus {
  outline: none;
}
.hub-cmd__esc:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
html.dichte-touch .hub-cmd__esc::before {
  content: '';
  position: absolute;
  top: -10px;
  right: -4px;
  bottom: -10px;
  left: -4px;
}
</style>

<style>
/* Dialogfläche (v-dialog hängt am [data-app]; anders als bei v-menu trägt das innere .v-dialog kein Scope-Attribut).
   Selektor stärker als „.shell .v-dialog“ (vuetify.css). Oben auf der Höhe, auf der der volle Dialog
   (48 + 300 + Kanten) mittig stünde; beim Filtern schrumpft er nach unten. */
.v-dialog__content > .v-dialog.app-hub-search-dialog {
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  box-sizing: border-box;
  width: 100%;
  max-height: calc(100% - 32px);
  margin: max(16px, calc((100vh - 352px) / 2)) 16px 16px;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface-raised);
  box-shadow: var(--sh-overlay);
}
@media (max-width: 639.98px) {
  .v-dialog__content > .v-dialog.app-hub-search-dialog {
    margin-top: 16px;
  }
}
</style>
